import React from "react";
import { motion } from "framer-motion";
import {
  Brain,
  Activity,
  BarChart3,
  ShieldCheck,
  Play,
  Globe2,
  Droplets,
  LineChart,
  Sparkles,
  Gauge,
  CalendarRange,
  Newspaper,
  Zap,
} from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { ASSETS, LINKS } from "../assets";
import { DiplomaSection } from "./trendia/DiplomaSection";

const featureIcons = [Brain, Activity, BarChart3, ShieldCheck];
const galleryIcons = [Droplets, LineChart, Sparkles, Gauge, CalendarRange, Newspaper, Zap];
const galleryAccents = [
  { glow: "rgba(0,229,255,0.45)", chip: "from-cyan-500/20 to-cyan-400/10", text: "text-cyan-300", ring: "ring-cyan-400/30" },
  { glow: "rgba(168,85,247,0.45)", chip: "from-purple-500/20 to-purple-400/10", text: "text-purple-300", ring: "ring-purple-400/30" },
  { glow: "rgba(236,72,153,0.45)", chip: "from-pink-500/20 to-pink-400/10", text: "text-pink-300", ring: "ring-pink-400/30" },
  { glow: "rgba(255,140,0,0.45)", chip: "from-orange-500/20 to-amber-400/10", text: "text-orange-300", ring: "ring-orange-400/30" },
  { glow: "rgba(132,204,22,0.45)", chip: "from-lime-500/20 to-green-400/10", text: "text-lime-300", ring: "ring-lime-400/30" },
  { glow: "rgba(56,189,248,0.45)", chip: "from-sky-500/20 to-blue-400/10", text: "text-sky-300", ring: "ring-sky-400/30" },
  { glow: "rgba(250,204,21,0.45)", chip: "from-yellow-500/20 to-amber-400/10", text: "text-yellow-300", ring: "ring-yellow-400/30" },
];

export const TrendIASection = () => {
  const { t } = useLanguage();

  return (
    <section
      id="trendia"
      className="relative w-full overflow-hidden bg-[#040406] py-24 md:py-32 lg:py-40"
      data-testid="trendia-section"
    >
      {/* Background glows */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_20%,rgba(168,85,247,0.18),transparent_55%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_80%,rgba(0,229,255,0.14),transparent_55%)]" />
      <div className="absolute left-1/2 top-0 h-px w-3/4 -translate-x-1/2 bg-gradient-to-r from-transparent via-cyan-400/30 to-transparent" />

      <div className="relative mx-auto max-w-7xl px-6 md:px-12">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12 lg:gap-12">
          {/* LEFT: content */}
          <div className="lg:col-span-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-400/5 px-3 py-1.5 backdrop-blur"
              data-testid="trendia-eyebrow"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-cyan-400 shadow-[0_0_8px_rgba(0,229,255,0.8)]" />
              <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-cyan-200 md:text-xs">
                {t.trendia.eyebrow}
              </span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mt-6 flex items-center gap-4"
            >
              <img
                src={ASSETS.logos.trendia}
                alt="TrendIA"
                className="h-16 w-16 rounded-2xl object-cover shadow-[0_0_40px_rgba(168,85,247,0.45)] md:h-20 md:w-20"
                data-testid="trendia-logo"
              />
              <h2 className="font-display bg-gradient-to-r from-cyan-300 via-fuchsia-400 to-purple-400 bg-clip-text text-4xl font-black tracking-tight text-transparent md:text-6xl">
                {t.trendia.title}
              </h2>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="mt-4 text-xl font-semibold text-white md:text-2xl"
            >
              {t.trendia.tagline}
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-5 max-w-xl text-base leading-relaxed text-white/55 md:text-lg"
            >
              {t.trendia.description}
            </motion.p>

            {/* Features grid */}
            <div className="mt-10 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {t.trendia.features.map((f, i) => {
                const Icon = featureIcons[i] || Brain;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.5, delay: 0.1 * i }}
                    className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-4 backdrop-blur-xl transition-all hover:border-cyan-400/30 hover:bg-white/[0.05]"
                    data-testid={`trendia-feature-${i}`}
                  >
                    <div className="mb-2 flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500/20 to-purple-500/20 ring-1 ring-white/10">
                      <Icon className="h-4 w-4 text-cyan-300" />
                    </div>
                    <h4 className="text-sm font-bold text-white">{f.title}</h4>
                    <p className="mt-1 text-xs text-white/55">{f.desc}</p>
                  </motion.div>
                );
              })}
            </div>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-10 flex flex-col gap-3 sm:flex-row"
            >
              <a
                href={LINKS.trendia_play}
                target="_blank"
                rel="noreferrer"
                className="group relative inline-flex items-center justify-center gap-3 overflow-hidden rounded-full bg-white px-6 py-3.5 text-sm font-bold text-black transition-all hover:-translate-y-0.5 hover:shadow-[0_0_30px_rgba(255,255,255,0.4)] md:text-base"
                data-testid="trendia-play-store-btn"
              >
                <Play className="h-4 w-4 fill-black" />
                <span className="flex flex-col items-start leading-tight">
                  <span className="text-[9px] font-medium uppercase tracking-widest text-black/60">
                    {t.trendia.coming_soon}
                  </span>
                  <span>{t.trendia.download_play}</span>
                </span>
              </a>

              <a
                href={LINKS.trendia_web}
                target="_blank"
                rel="noreferrer"
                className="group relative inline-flex items-center justify-center gap-3 overflow-hidden rounded-full border border-cyan-400/40 bg-black px-6 py-3.5 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:border-cyan-400 hover:shadow-[0_0_30px_rgba(0,229,255,0.45)] md:text-base"
                data-testid="trendia-web-btn"
              >
                <Globe2 className="h-4 w-4 text-cyan-300" />
                <span className="flex flex-col items-start leading-tight">
                  <span className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest text-emerald-400">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                      <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    </span>
                    {t.trendia.available_now}
                  </span>
                  <span>{t.trendia.download_web}</span>
                </span>
              </a>
            </motion.div>

            {/* REBEL PROOF™ · on-chain diploma section (additive, motion wraps internal) */}
            <DiplomaSection />
          </div>

          {/* RIGHT: screenshots */}
          <div className="relative lg:col-span-6">
            <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_center,rgba(168,85,247,0.35),transparent_70%)] blur-2xl" />
            <div className="relative mx-auto grid h-full w-full max-w-2xl grid-cols-12 gap-3">
              <motion.div
                initial={{ opacity: 0, y: 60, rotate: -6 }}
                whileInView={{ opacity: 1, y: 0, rotate: -4 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="col-span-12 sm:col-span-6 sm:mt-12"
              >
                <img
                  src={ASSETS.trendia_screens[0]}
                  alt="TrendIA Liquidaciones"
                  className="w-full rounded-2xl ring-1 ring-white/10 shadow-[0_30px_60px_rgba(168,85,247,0.35)]"
                  data-testid="trendia-screen-0"
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
                className="col-span-12 sm:col-span-6 sm:-mt-2"
              >
                <img
                  src={ASSETS.trendia_screens[1]}
                  alt="TrendIA Análisis"
                  className="w-full rounded-2xl ring-1 ring-white/10 shadow-[0_30px_60px_rgba(0,229,255,0.3)]"
                  data-testid="trendia-screen-1"
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 60, rotate: 6 }}
                whileInView={{ opacity: 1, y: 0, rotate: 3 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
                className="col-span-12 mx-auto w-[85%] sm:w-[70%]"
              >
                <img
                  src={ASSETS.trendia_screens[2]}
                  alt="TrendIA IA Traders"
                  className="w-full rounded-2xl ring-1 ring-white/10 shadow-[0_30px_60px_rgba(236,72,153,0.35)]"
                  data-testid="trendia-screen-2"
                />
              </motion.div>
            </div>
          </div>
        </div>

        {/* CAPABILITIES GALLERY — 7 capturas únicas con descripciones */}
        <div className="mt-28 md:mt-36">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="mx-auto max-w-3xl text-center"
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-purple-400/20 bg-purple-400/5 px-3 py-1.5 backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-purple-400 shadow-[0_0_8px_rgba(168,85,247,0.8)]" />
              <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-purple-200 md:text-xs">
                {t.trendia.gallery_eyebrow}
              </span>
            </div>
            <h3 className="font-display mt-5 text-3xl font-black tracking-tighter text-white md:text-5xl">
              {t.trendia.gallery_title}
            </h3>
            <p className="mx-auto mt-4 max-w-xl text-sm text-white/55 md:text-base">
              {t.trendia.gallery_subtitle}
            </p>
          </motion.div>

          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
            {t.trendia.gallery.map((g, i) => {
              const Icon = galleryIcons[i] || Brain;
              const a = galleryAccents[i] || galleryAccents[0];
              const fromLeft = i % 2 === 0;
              return (
                <motion.article
                  key={i}
                  initial={{ opacity: 0, x: fromLeft ? -120 : 120, rotate: fromLeft ? -2 : 2 }}
                  whileInView={{ opacity: 1, x: 0, rotate: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.9, delay: 0.05 * i, ease: [0.22, 1, 0.36, 1] }}
                  whileHover={{ y: -6 }}
                  className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-xl transition-colors hover:border-white/20"
                  data-testid={`trendia-gallery-card-${i}`}
                  style={{ ["--glow"]: a.glow }}
                >
                  {/* Hover glow */}
                  <div
                    className="pointer-events-none absolute -inset-px rounded-3xl opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                    style={{
                      background: `radial-gradient(600px circle at 50% 0%, var(--glow), transparent 60%)`,
                    }}
                  />

                  {/* Image */}
                  <div className="relative aspect-square w-full overflow-hidden bg-gradient-to-br from-black via-zinc-950 to-black">
                    <motion.img
                      src={ASSETS.trendia_gallery[i]}
                      alt={g.title}
                      className="absolute inset-0 h-full w-full object-contain p-2"
                      initial={{ scale: 1.04, opacity: 0.85 }}
                      whileInView={{ scale: 1, opacity: 1 }}
                      viewport={{ once: true, margin: "-80px" }}
                      transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                      whileHover={{ scale: 1.03, transition: { duration: 0.8 } }}
                    />
                    {/* Soft top vignette only — keep image fully visible */}
                    <div className="pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-black/60 to-transparent" />
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/40 to-transparent" />
                    {/* Top tag chip */}
                    <div
                      className={`absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-gradient-to-br ${a.chip} px-3 py-1 ring-1 ${a.ring} backdrop-blur-md`}
                    >
                      <Icon className={`h-3.5 w-3.5 ${a.text}`} />
                      <span className={`text-[10px] font-bold uppercase tracking-[0.2em] ${a.text}`}>
                        {g.tag}
                      </span>
                    </div>
                    {/* Number badge */}
                    <div className="absolute right-4 top-4 rounded-full border border-white/10 bg-black/50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-white/60 backdrop-blur-md">
                      {String(i + 1).padStart(2, "0")} / 07
                    </div>
                    {/* Animated shimmer line on hover */}
                    <span className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent opacity-0 transition-opacity duration-700 group-hover:opacity-100" />
                  </div>

                  {/* Content */}
                  <div className="relative p-6 md:p-7">
                    <h4 className="font-display text-lg font-bold leading-snug text-white md:text-xl">
                      {g.title}
                    </h4>
                    <p className="mt-2.5 text-sm leading-relaxed text-white/55">{g.desc}</p>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
